import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import AppStorage from "@/utils/helpers";

/**
 * User interface combining both auth implementations
 */
export interface User {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  telephone?: string;
  street?: string;
  city?: string;
  state?: string;
  date_of_birth?: string;
  role?: string;
  accessToken?: string;
  createdAt?: string;
  updatedAt?: string;
  tenant_id?: {
    _id: string;
    name: string;
    logo: string;
    primary_color: string;
    secondary_color: Array<string>;
  };
  // Legacy fields for compatibility
  fullName?: string;
  phone?: string;
  birthday?: string;
  // Guest user fields
  isGuest?: boolean;
}

/**
 * Guest user interface for temporary storage
 */
export interface GuestUser {
  email: string;
  phoneNumber: string;
  fullName?: string;
  isGuest: true;
  tempId: string;
  address?: string; // Add address field
}

interface AuthState {
  user: User | null;
  guestUser: GuestUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  deliveryAddress: string | null; // Add delivery address state
}

/**
 * Load user authentication state from storage
 */
const loadUserFromAppStorage = (): boolean => {
  if (typeof window !== "undefined") {
    const token = AppStorage.get("atk");
    return !!token;
  }
  return false;
};

/**
 * Load user data from storage
 */
const loadUserDataFromAppStorage = (): User | null => {
  if (typeof window !== "undefined") {
    const user = AppStorage.get("user");
    return user ? JSON.parse(user) : null;
  }
  return null;
};

/**
 * Load guest user data from storage
 */
const loadGuestUserFromAppStorage = (): GuestUser | null => {
  if (typeof window !== "undefined") {
    const guestUser = AppStorage.get("guestUser");
    return guestUser ? JSON.parse(guestUser) : null;
  }
  return null;
};

/**
 * Save user data to storage
 */
const saveUserToAppStorage = (user: User) => {
  AppStorage.save("user", JSON.stringify(user));
};

/**
 * Save guest user data to storage
 */
const saveGuestUserToAppStorage = (guestUser: GuestUser) => {
  AppStorage.save("guestUser", JSON.stringify(guestUser));
};

/**
 * Load delivery address from storage
 */
const loadDeliveryAddressFromAppStorage = (): string | null => {
  if (typeof window !== "undefined") {
    const address = AppStorage.get("deliveryAddress");
    return address || null;
  }
  return null;
};

/**
 * Save delivery address to storage
 */
const saveDeliveryAddressToAppStorage = (address: string) => {
  AppStorage.save("deliveryAddress", address);
};

const initialState: AuthState = {
  user: loadUserDataFromAppStorage(),
  guestUser: loadGuestUserFromAppStorage(),
  isAuthenticated: loadUserFromAppStorage(),
  loading: false,
  deliveryAddress: loadDeliveryAddressFromAppStorage(), // Add to initial state
};

/**
 * Auth slice managing user authentication state
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.loading = false;
      if (action.payload) {
        saveUserToAppStorage(action.payload);
      }
    },
    signinUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.loading = false;
      // Clear guest user when signing in
      state.guestUser = null;
      AppStorage.remove("guestUser");
      saveUserToAppStorage(action.payload);
    },
    setGuestUser: (state, action: PayloadAction<GuestUser>) => {
      state.guestUser = action.payload;
      state.loading = false;
      saveGuestUserToAppStorage(action.payload);
    },
    clearGuestUser: (state) => {
      state.guestUser = null;
      AppStorage.remove("guestUser");
    },
    setDeliveryAddress: (state, action: PayloadAction<string>) => {
      state.deliveryAddress = action.payload;
      saveDeliveryAddressToAppStorage(action.payload);
    },
    clearDeliveryAddress: (state) => {
      state.deliveryAddress = null;
      AppStorage.remove("deliveryAddress");
    },
    logout: (state) => {
      state.user = null;
      state.guestUser = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.deliveryAddress = null; // Clear address on logout
      AppStorage.remove("user");
      AppStorage.remove("guestUser");
      AppStorage.remove("deliveryAddress");
      AppStorage.remove("atk");
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    finishLoading: (state) => {
      state.loading = false;
    },
  },
});

export const {
  setUser,
  signinUser,
  setGuestUser,
  clearGuestUser,
  setDeliveryAddress, // Export new action
  clearDeliveryAddress, // Export new action
  logout,
  setLoading,
  finishLoading,
} = authSlice.actions;

// Add new selector
export const selectDeliveryAddress = (state: {
  auth: AuthState;
}): string | null => state.auth.deliveryAddress;
// Selectors
export const selectUser = (state: { auth: AuthState }): User | null =>
  state.auth.user;
export const selectGuestUser = (state: { auth: AuthState }): GuestUser | null =>
  state.auth.guestUser;
export const selectIsAuthenticated = (state: { auth: AuthState }): boolean =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }): boolean =>
  state.auth.loading;

export default authSlice.reducer;
